/**
 * Where the participant "Contact researcher" action should send the user.
 *
 * The server builds `contactUrl` as the legacy messages page with a
 * `conversationUid` query param — empty when no conversation exists yet:
 * `${websiteUrl}/respondents/v2/messages/?…&conversationUid=<uid|"">`.
 *
 * Only the query params matter now: with the native inbox fully rolled out,
 * an existing conversation opens directly by uid, and a project with no
 * thread yet deep-links the inbox's compose flow via `surveyResponseId` /
 * `projectId` (the messages page resolves them to a conversation or opens
 * compose preselected with the project).
 */
export type ContactResearcherTarget =
  | { kind: 'conversation'; conversationUid: string }
  | { kind: 'compose'; projectId: string; surveyResponseId?: string };

/** Pull a non-empty query param out of the (legacy-shaped) contact URL. */
function extractParam(
  contactUrl: string | undefined,
  name: string,
): string | undefined {
  if (!contactUrl) return undefined;
  let value: string | null = null;
  try {
    value = new URL(contactUrl).searchParams.get(name);
  } catch {
    // Not an absolute URL we can parse — treat as "param not present".
    return undefined;
  }
  return value && value.trim().length > 0 ? value : undefined;
}

/**
 * Resolve the destination for "Contact researcher". `projectId` is the
 * caller's own project id, used as the compose fallback when the contact URL
 * is missing or unparseable — the messages page then resolves it to an
 * existing thread or lands on the inbox.
 */
export function resolveContactResearcherTarget(
  contactUrl: string | undefined,
  projectId: string,
): ContactResearcherTarget {
  const conversationUid = extractParam(contactUrl, 'conversationUid');
  if (conversationUid) return { kind: 'conversation', conversationUid };

  return {
    kind: 'compose',
    projectId: extractParam(contactUrl, 'projectId') ?? projectId,
    surveyResponseId: extractParam(contactUrl, 'surveyResponseId'),
  };
}
